import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { requestPasswordReset } from '../api/account';
import { apiErrorMessage } from '../api/errorMessage';
import { validateEmail } from '../auth/validation';

/// "Forgot your password?" page. Asks for the user's email and POSTs to
/// `/account/password-reset/request`, then shows a confirmation message
/// regardless of whether the email was on file (the server returns 204
/// either way; mirroring that here keeps the panel from leaking which
/// emails are registered).
export default function ResetRequest() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    const emailCheck = validateEmail(email);
    if (!emailCheck.ok) {
      setError(emailCheck.message);
      return;
    }

    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSubmitted(true);
    } catch (err) {
      // 4xx for malformed body → show inline. Any other error → generic.
      // The 204 success path is the same shape regardless of whether the
      // email actually maps to an account, so we don't distinguish here.
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Reset your password</h1>
          {!submitted && (
            <p>Enter your email and we'll send a reset link.</p>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {submitted ? (
          <div className="info-message">
            <p>
              If <strong>{email.trim()}</strong> matches an account, we've sent
              a reset link. It expires in one hour.
            </p>
            <p className="hint">
              Didn't get an email? Check your spam folder, then try again.
            </p>
            <div className="auth-link-block">
              <Link to="/login" className="btn-link">
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  autoComplete="email"
                  maxLength={254}
                  disabled={loading}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <div className="auth-link-row-spacious">
              <Link to="/login" className="btn-link">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
